import React from 'react';
import { useTranslation } from 'react-i18next';
import { GameState } from '../core/types';
import { GameOverReason } from '../core/engine';

interface Props {
  gameState: GameState;
  reason: GameOverReason;
  onRestart: () => void;
}

const REASON_META: Record<GameOverReason, { icon: string; accentClass: string }> = {
  kpi_zero:          { icon: '📂', accentClass: 'gameover-accent-gray' },
  career_risk:       { icon: '🔍', accentClass: 'gameover-accent-amber' },
  procedural_risk:   { icon: '⚖️', accentClass: 'gameover-accent-red' },
  all_cases_completed: { icon: '✨', accentClass: 'gameover-accent-blue' },
};

const GameOverScreen: React.FC<Props> = ({ gameState, reason, onRestart }) => {
  const { t } = useTranslation();
  const { icon, accentClass } = REASON_META[reason];

  return (
    <div className="gameover-screen">
      <div className={`gameover-card ${accentClass}`}>
        <div className="gameover-icon">{icon}</div>

        <h1 className="gameover-title">{t(`gameOver.${reason}.title`)}</h1>
        <p className="gameover-subtitle">{t(`gameOver.${reason}.subtitle`)}</p>

        <p className="gameover-narrative">{t(`gameOver.${reason}.narrative`)}</p>

        <div className="gameover-stats">
          <div className="gameover-stat">
            <span className="gameover-stat-label">{t('gameOver.daysWorked')}</span>
            <span className="gameover-stat-value">{gameState.currentDay}</span>
          </div>
          <div className="gameover-stat">
            <span className="gameover-stat-label">{t('gameOver.casesProcessed')}</span>
            <span className="gameover-stat-value">{gameState.totalProcessed}</span>
          </div>
          <div className="gameover-stat">
            <span className="gameover-stat-label">KPI</span>
            <span className={`gameover-stat-value ${gameState.kpi <= 20 ? 'danger' : ''}`}>
              {gameState.kpi}
            </span>
          </div>
          <div className="gameover-stat">
            <span className="gameover-stat-label">{t('topbar.careerRisk')}</span>
            <span className={`gameover-stat-value ${gameState.careerRisk >= 80 ? 'danger' : ''}`}>
              {gameState.careerRisk}
            </span>
          </div>
          <div className="gameover-stat">
            <span className="gameover-stat-label">{t('topbar.proceduralRisk')}</span>
            <span className={`gameover-stat-value ${gameState.proceduralRisk >= 80 ? 'danger' : ''}`}>
              {gameState.proceduralRisk}
            </span>
          </div>
        </div>

        <div className="gameover-actions">
          <button className="gameover-restart-btn" onClick={onRestart}>
            {reason === 'all_cases_completed' ? t('gameOver.playAgain') : t('gameOver.restart')}
          </button>
          <button
            className="gameover-leaderboard-btn"
            onClick={() => { window.location.search = '?leaderboard'; }}
          >
            {t('gameOver.leaderboard')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOverScreen;
